import maplibregl, { type Map as MapLibreMap, type Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { Loader2Icon, MapPinIcon } from 'lucide-react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  MapPickupPoint,
  PickupPointSource,
} from '@/lib/eshop/pickupPointMap'
import {
  geocodeAddress,
  getMapyMapStyle,
  type GeocodeAddressInput,
  type LatLng,
} from '@/lib/mapy'
import { cn } from '@/lib/utils'

const CZ_CENTER: LatLng = { latitude: 49.8, longitude: 15.5 }
const DEFAULT_ZOOM = 12
const FALLBACK_ZOOM = 7
const MOVE_RELOAD_DEBOUNCE_MS = 500
const MOVE_THRESHOLD_DEG = 0.02
const DEFAULT_LIMIT = 50
const DEFAULT_RADIUS_KM = 20

type PickupPointMapProps = {
  source: PickupPointSource
  customerAddress?: GeocodeAddressInput | null
  selectedId: string | null
  onSelect: (id: string) => void
  className?: string
  /** When false, map drag does not re-fetch (e.g. personal pickup). Default true. */
  reloadOnMove?: boolean
}

function createMarkerElement(
  kind: 'customer' | 'pickup' | 'selected',
): HTMLDivElement {
  const el = document.createElement('div')
  el.className = cn(
    'rounded-full border-2 border-white shadow-sm cursor-pointer',
    kind === 'customer' && 'size-3.5 bg-blue-600',
    kind === 'pickup' && 'size-4 bg-orange-500',
    kind === 'selected' && 'size-5 bg-primary ring-2 ring-primary/40',
  )
  return el
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

export function PickupPointMap({
  source,
  customerAddress,
  selectedId,
  onSelect,
  className,
  reloadOnMove = true,
}: PickupPointMapProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.tester.delivery.pickupMap',
  })

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const markersRef = useRef<Marker[]>([])
  const customerMarkerRef = useRef<Marker | null>(null)
  const listItemRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const lastFetchCenterRef = useRef<LatLng | null>(null)
  const moveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialCenterAppliedRef = useRef(false)

  const [mapReady, setMapReady] = useState(false)
  const [center, setCenter] = useState<LatLng | null>(null)
  const [centerFromAddress, setCenterFromAddress] = useState(false)
  const [points, setPoints] = useState<MapPickupPoint[]>([])
  const [loadingCenter, setLoadingCenter] = useState(true)
  const [loadingPoints, setLoadingPoints] = useState(false)
  const [geocodeFailed, setGeocodeFailed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadPoints = useEffectEvent(async (fetchCenter: LatLng) => {
    setLoadingPoints(true)
    setError(null)
    lastFetchCenterRef.current = fetchCenter

    try {
      const loaded = await source.load({
        center: fetchCenter,
        limit: DEFAULT_LIMIT,
        radiusKm: DEFAULT_RADIUS_KM,
      })
      setPoints(loaded)
    } catch (err) {
      setPoints([])
      setError(err instanceof Error ? err.message : t('loadError'))
    } finally {
      setLoadingPoints(false)
    }
  })

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    const resolveCenter = async () => {
      setLoadingCenter(true)
      setGeocodeFailed(false)

      const hasAddress = Boolean(
        customerAddress?.street?.trim() ||
          customerAddress?.city?.trim() ||
          customerAddress?.zip?.trim(),
      )

      if (!hasAddress) {
        if (!cancelled) {
          setCenter(CZ_CENTER)
          setCenterFromAddress(false)
          setLoadingCenter(false)
        }
        return
      }

      try {
        const result = await geocodeAddress(customerAddress!, controller.signal)
        if (cancelled) return

        if (result) {
          setCenter(result)
          setCenterFromAddress(true)
        } else {
          setCenter(CZ_CENTER)
          setCenterFromAddress(false)
          setGeocodeFailed(true)
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (!cancelled) {
          setCenter(CZ_CENTER)
          setCenterFromAddress(false)
          setGeocodeFailed(true)
        }
      } finally {
        if (!cancelled) setLoadingCenter(false)
      }
    }

    void resolveCenter()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    customerAddress?.street,
    customerAddress?.city,
    customerAddress?.zip,
    customerAddress?.country,
  ])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getMapyMapStyle(),
      center: [CZ_CENTER.longitude, CZ_CENTER.latitude],
      zoom: FALLBACK_ZOOM,
    })

    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right',
    )

    class LogoControl implements maplibregl.IControl {
      private container: HTMLDivElement | undefined

      onAdd() {
        this.container = document.createElement('div')
        this.container.className = 'maplibregl-ctrl'
        this.container.innerHTML =
          '<a href="https://mapy.com/" target="_blank" rel="noreferrer"><img src="https://api.mapy.com/img/api/logo.svg" alt="Mapy.com" /></a>'
        return this.container
      }

      onRemove() {
        this.container?.parentNode?.removeChild(this.container)
        this.container = undefined
      }
    }

    map.on('load', () => {
      map.addControl(new LogoControl(), 'bottom-left')
      setMapReady(true)
    })

    mapRef.current = map

    return () => {
      if (moveTimerRef.current) clearTimeout(moveTimerRef.current)
      markersRef.current.forEach((m) => m.remove())
      markersRef.current = []
      customerMarkerRef.current?.remove()
      customerMarkerRef.current = null
      map.remove()
      mapRef.current = null
      setMapReady(false)
      initialCenterAppliedRef.current = false
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !center) return

    map.jumpTo({
      center: [center.longitude, center.latitude],
      zoom: centerFromAddress ? DEFAULT_ZOOM : FALLBACK_ZOOM,
    })
    initialCenterAppliedRef.current = true
  }, [center, centerFromAddress, mapReady])

  useEffect(() => {
    if (!center || loadingCenter) return
    void loadPoints(center)
  }, [center, loadingCenter])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !reloadOnMove) return

    const onMoveEnd = () => {
      if (!initialCenterAppliedRef.current) return
      if (moveTimerRef.current) clearTimeout(moveTimerRef.current)

      moveTimerRef.current = setTimeout(() => {
        const c = map.getCenter()
        const next: LatLng = { latitude: c.lat, longitude: c.lng }
        const prev = lastFetchCenterRef.current

        if (
          prev &&
          Math.abs(prev.latitude - next.latitude) < MOVE_THRESHOLD_DEG &&
          Math.abs(prev.longitude - next.longitude) < MOVE_THRESHOLD_DEG
        ) {
          return
        }

        void loadPoints(next)
      }, MOVE_RELOAD_DEBOUNCE_MS)
    }

    map.on('moveend', onMoveEnd)

    return () => {
      map.off('moveend', onMoveEnd)
      if (moveTimerRef.current) clearTimeout(moveTimerRef.current)
    }
  }, [reloadOnMove, mapReady])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return

    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    for (const point of points) {
      const isSelected = point.id === selectedId
      const marker = new maplibregl.Marker({
        element: createMarkerElement(isSelected ? 'selected' : 'pickup'),
      })
        .setLngLat([point.longitude, point.latitude])
        .setPopup(
          new maplibregl.Popup({ offset: 12, closeButton: false }).setHTML(
            `<strong>${escapeHtml(point.name)}</strong><br/><span style="font-size:12px;opacity:.8">${escapeHtml(
              [point.street, point.city].filter(Boolean).join(', '),
            )}</span>`,
          ),
        )
        .addTo(map)

      marker.getElement().addEventListener('click', (e) => {
        e.stopPropagation()
        onSelect(point.id)
      })

      markersRef.current.push(marker)
    }

    customerMarkerRef.current?.remove()
    customerMarkerRef.current = null

    if (center && centerFromAddress) {
      customerMarkerRef.current = new maplibregl.Marker({
        element: createMarkerElement('customer'),
      })
        .setLngLat([center.longitude, center.latitude])
        .setPopup(
          new maplibregl.Popup({ offset: 10, closeButton: false }).setText(
            t('customerLocation'),
          ),
        )
        .addTo(map)
    }
  }, [points, selectedId, center, centerFromAddress, onSelect, t, mapReady])

  useEffect(() => {
    if (!selectedId) return

    const item = listItemRefs.current.get(selectedId)
    if (!item) return

    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedId])

  const isLoading = loadingCenter || loadingPoints

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {geocodeFailed && (
        <p className="text-sm text-muted-foreground">{t('geocodeFailed')}</p>
      )}

      <div className="grid gap-3 md:grid-cols-[1fr_280px]">
        <div className="relative overflow-hidden rounded-lg border">
          <div ref={mapContainerRef} className="h-[400px] w-full" />
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50">
              <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        <div className="flex max-h-[400px] flex-col gap-1 overflow-auto rounded-lg border p-2">
          <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">
            {t('listTitle')}
          </p>

          {error && (
            <p className="px-2 py-3 text-sm text-destructive">{error}</p>
          )}

          {!error && !isLoading && points.length === 0 && (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              {t('empty')}
            </p>
          )}

          {points.map((point) => {
            const selected = point.id === selectedId
            return (
              <Button
                key={point.id}
                ref={(element) => {
                  if (element) {
                    listItemRefs.current.set(point.id, element)
                  } else {
                    listItemRefs.current.delete(point.id)
                  }
                }}
                type="button"
                variant={selected ? 'secondary' : 'ghost'}
                className={cn(
                  'h-auto justify-start gap-2 px-2 py-2 text-left',
                  selected && 'ring-1 ring-primary/30',
                )}
                onClick={() => onSelect(point.id)}
              >
                <MapPinIcon
                  className={cn(
                    'mt-0.5 size-4 shrink-0',
                    selected ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium">
                    {point.name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {[point.street, point.city].filter(Boolean).join(', ')}
                    {point.distanceKm !== undefined
                      ? ` · ${t('distance', { km: point.distanceKm.toFixed(1) })}`
                      : ''}
                  </span>
                </span>
              </Button>
            )
          })}
        </div>
      </div>

      {!selectedId && (
        <p className="text-sm text-muted-foreground">{t('selectPrompt')}</p>
      )}
    </div>
  )
}
